import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, UserPlus, Sparkles, Loader2, AlertTriangle, Ticket } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Loading } from "@/components/site/ui";
import { Panel, AdminHeading, Th, Td } from "@/components/admin/ui";

const STATUS_STYLES = {
  "Pending Activation": "text-amber-300 border-amber-400/30 bg-amber-400/5",
  "More Information Needed": "text-sky-300 border-sky-400/30 bg-sky-400/5",
  "Active": "text-emerald-300 border-emerald-400/30 bg-emerald-400/5",
  "Approved": "text-emerald-300 border-emerald-400/30 bg-emerald-400/5",
  "Declined": "text-rose-300 border-rose-400/30 bg-rose-400/5",
  "Withdrawn": "text-muted-foreground border-white/15",
};
const FILTERS = ["Pending Activation", "More Information Needed", "Approved", "Declined", "Withdrawn", ""];

export default function AdminAccessRequests() {
  const [reqs, setReqs] = useState(null);
  const [filter, setFilter] = useState("Pending Activation");
  const [sort, setSort] = useState("newest");
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [comms, setComms] = useState([]);

  const load = () => {
    const q = new URLSearchParams();
    if (filter) q.set("status", filter);
    q.set("sort", sort);
    api.get(`/admin/access-requests?${q.toString()}`).then((r) => setReqs(r.data)).catch(() => setReqs([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, sort]);

  const approve = async (r) => {
    setBusy(r.id);
    try { await api.post(`/admin/access-requests/${r.id}/approve`); toast.success(`${r.first_name || r.email} approved.`); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const decline = async (r) => {
    const reason = window.prompt("Reason for declining (optional):", "");
    if (reason === null) return;
    setBusy(r.id);
    try { await api.post(`/admin/access-requests/${r.id}/decline`, { reason }); toast.success("Request declined."); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const setStatus = async (r, status, notify) => {
    setBusy(r.id);
    try { await api.post(`/admin/access-requests/${r.id}/status`, { status, notify }); toast.success(`Marked "${status}".`); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const openDetail = (r) => {
    setDetail(r); setAi(null);
    api.get(`/admin/access-requests/${r.id}/communications`).then((res) => setComms(res.data)).catch(() => setComms([]));
  };
  const summarize = async () => {
    setAiBusy(true); setAi(null);
    try { const { data } = await api.post(`/admin/access-requests/${detail.id}/ai-summarize`); setAi({ label: data.label, text: data.suggestion }); }
    catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };
  const draft = async (kind) => {
    setAiBusy(true); setAi(null);
    try {
      const { data } = await api.post(`/admin/access-requests/${detail.id}/ai-draft`, { kind });
      setAi({ label: data.label, text: data.body, persisted: true });
      toast.success("D draft created in Communications — review & send there.");
      const res = await api.get(`/admin/access-requests/${detail.id}/communications`);
      setComms(res.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); }
  };

  if (reqs === null) return <Loading />;

  return (
    <div className="max-w-6xl" data-testid="admin-access-requests">
      <AdminHeading title="Access Requests" subtitle="Review and qualify invitation-only account requests. Approval is always human-controlled." />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {FILTERS.map((t) => (
          <button key={t || "all"} onClick={() => setFilter(t)} data-testid={`access-filter-${(t || "all").toLowerCase().replace(/\s/g, "-")}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === t ? "border-gold text-gold bg-gold/10" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>
            {t || "All"}
          </button>
        ))}
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="access-sort" className="ml-auto text-xs bg-black/40 border border-white/15 rounded-full px-3 py-1.5 text-muted-foreground">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
      </div>
      <Panel>
        {reqs.length === 0 ? (
          <div className="py-14 text-center text-muted-foreground">
            <UserPlus className="mx-auto mb-3 opacity-40" size={28} />
            <p className="text-sm">No {filter ? filter.toLowerCase() : ""} requests.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-left">
                <Th>Name</Th><Th>Email</Th><Th>Interest</Th><Th>Requested</Th><Th>Flags</Th><Th>Status</Th><Th></Th>
              </tr></thead>
              <tbody>
                {reqs.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => openDetail(r)} data-testid={`access-row-${r.id}`}>
                    <Td>{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</Td>
                    <Td className="text-muted-foreground">{r.email}</Td>
                    <Td className="max-w-[160px] truncate text-muted-foreground">{r.interest || r.preferred_experience || "—"}</Td>
                    <Td className="text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</Td>
                    <Td>
                      <span className="flex gap-1">
                        {r.invitation_code && <Ticket size={13} className="text-gold" title="Invitation linked" />}
                        {r.restricted_warning && <AlertTriangle size={13} className="text-rose-400" title="Restricted match" />}
                        {r.duplicate_warning && <AlertTriangle size={13} className="text-amber-400" title="Possible duplicate" />}
                      </span>
                    </Td>
                    <Td><span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[r.status] || "text-muted-foreground border-white/15"}`}>{r.status}</span></Td>
                    <Td className="text-gold text-xs">Review →</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="glass-dark border border-gold/30 rounded-2xl max-w-2xl w-full mt-10 p-6" onClick={(e) => e.stopPropagation()} data-testid="access-detail">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-serif text-2xl">{[detail.first_name, detail.last_name].filter(Boolean).join(" ")}</h3>
                <p className="text-sm text-muted-foreground">{detail.email}{detail.phone ? ` · ${detail.phone}` : ""}</p>
                <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[detail.status] || "text-muted-foreground border-white/15"}`}>{detail.status}</span>
              </div>
              <button onClick={() => setDetail(null)} className="text-muted-foreground hover:text-gold"><X size={18} /></button>
            </div>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
              {[["Requested", fmtDate(detail.created_at)], ["Referral", detail.referral_source], ["Preferred dates", detail.preferred_dates], ["Celebration", detail.celebration], ["Guest count", detail.guest_count], ["Preferred experience", detail.preferred_experience], ["Contact method", detail.contact_method], ["Invitation code", detail.invitation_code]]
                .filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}><span className="text-muted-foreground">{k}:</span> {v}</div>
                ))}
            </div>
            {(detail.restricted_warning || detail.duplicate_warning) && (
              <div className="flex flex-wrap gap-2 mb-4">
                {detail.restricted_warning && <span className="text-[11px] text-rose-300 border border-rose-400/30 bg-rose-400/5 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle size={11} /> Restricted match — verify</span>}
                {detail.duplicate_warning && <span className="text-[11px] text-amber-300 border border-amber-400/30 bg-amber-400/5 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle size={11} /> Possible duplicate</span>}
              </div>
            )}
            {detail.why_join && (
              <div className="mb-4">
                <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-1">Why they'd love to experience The D</p>
                <p className="text-sm glass rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid="access-why-join">{detail.why_join}</p>
              </div>
            )}

            <div className="border-t border-white/10 pt-3 mb-4">
              <div className="flex flex-wrap gap-2">
                <button onClick={summarize} disabled={aiBusy} className="btn-ghost-gold px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid="access-ai-summarize">{aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Summarize with D</button>
                <button onClick={() => draft("follow_up")} disabled={aiBusy} className="text-xs text-pool border border-pool/30 rounded-full px-3 py-1.5 hover:bg-pool/10">Draft follow-up</button>
                <button onClick={() => draft("more_info")} disabled={aiBusy} className="text-xs text-pool border border-pool/30 rounded-full px-3 py-1.5 hover:bg-pool/10">Draft "more info"</button>
                <button onClick={() => draft("approval")} disabled={aiBusy} className="text-xs text-pool border border-pool/30 rounded-full px-3 py-1.5 hover:bg-pool/10">Draft approval</button>
              </div>
              {ai && (
                <div className="mt-3 glass rounded-lg p-3" data-testid="access-ai-result">
                  <p className="text-[10px] tracking-label uppercase text-gold mb-1">{ai.label}</p>
                  <p className="text-sm whitespace-pre-wrap text-foreground/90">{ai.text}</p>
                  {ai.persisted
                    ? <p className="text-[11px] text-pool mt-2">Saved as a Draft in Communications → Review &amp; Release.</p>
                    : <button onClick={() => { navigator.clipboard?.writeText(ai.text); toast.success("Copied — review before sending."); }} className="text-[11px] text-muted-foreground hover:text-gold mt-2">Copy</button>}
                </div>
              )}
            </div>

            <div className="border-t border-white/10 pt-3 mb-4" data-testid="access-comms-history">
              <p className="text-[10px] tracking-label uppercase text-muted-foreground mb-2">Communications</p>
              {comms.length === 0 ? (
                <p className="text-xs text-muted-foreground">No communications yet. Use "Summarize / Draft with D" to create a reviewable draft.</p>
              ) : comms.map((cm) => (
                <div key={cm.id} className="glass rounded-lg px-3 py-2 text-sm mb-2 flex items-center gap-2" data-testid={`access-comm-${cm.id}`}>
                  <span className="flex-1 truncate">{cm.subject}</span>
                  {cm.source && cm.source.startsWith("d_") && <span className="text-[9px] text-gold border border-gold/30 rounded-full px-1.5">D</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/15 text-muted-foreground">{cm.status}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-white/10 pt-4">
              {["Pending Activation", "More Information Needed"].includes(detail.status) && (
                <>
                  <button disabled={busy === detail.id} onClick={() => approve(detail)} data-testid="access-approve" className="btn-gold px-4 py-2 text-xs inline-flex items-center gap-1"><Check size={13} /> Approve &amp; Activate</button>
                  <button disabled={busy === detail.id} onClick={() => setStatus(detail, "More Information Needed", true)} className="text-xs rounded-full border border-sky-400/40 text-sky-300 hover:bg-sky-400/10 px-4 py-2">Request more info</button>
                  <button disabled={busy === detail.id} onClick={() => decline(detail)} data-testid="access-decline" className="text-xs rounded-full border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 px-4 py-2 inline-flex items-center gap-1"><X size={13} /> Decline</button>
                </>
              )}
              {detail.status === "Approved" && <span className="text-xs text-muted-foreground">Approved{detail.reviewed_by ? ` by ${detail.reviewed_by}` : ""}.</span>}
              {detail.status === "Declined" && <span className="text-xs text-muted-foreground">Declined{detail.reviewed_by ? ` by ${detail.reviewed_by}` : ""}.</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
